/**
 * User invitation types and interfaces for API integration
 * Matches backend API models from UserInvitesApiModel.cs
 */

/**
 * Invitation status enum (matches backend InviteStatus)
 */
export enum InviteStatus {
  Pending = 0,
  Accepted = 1,
  Expired = 2,
  Revoked = 3,
}

/**
 * Request payload for creating a new user invitation
 */
export interface CreateUserInviteRequest {
  email: string
  role?: string
  customMessage?: string
  expirationDays: number
}

/**
 * Response from creating a user invitation
 */
export interface CreateUserInviteResponse {
  inviteId: string
  email: string
  expiresAt: string // ISO 8601 timestamp
  emailQueued: boolean
  inviteUrl: string
}

/**
 * Complete user invitation details
 */
export interface UserInviteResponse {
  id: string
  workspaceId: string
  email: string
  status: InviteStatus
  expiresAt: string // ISO 8601 timestamp
  invitedBy: string
  acceptedAt: string | null
  acceptedBy: string | null
  role: string | null
  customMessage: string | null
  createdOn: string
  createdBy: string
  updatedOn: string
  updatedBy: string
}

/**
 * List response for user invitations
 */
export interface UserInviteListResponse {
  invites: Array<UserInviteResponse>
  totalCount: number
}

/**
 * Request payload for accepting an invitation
 */
export interface AcceptUserInviteRequest {
  token: string
}

/**
 * Response from accepting an invitation
 */
export interface AcceptUserInviteResponse {
  workspaceId: string
  workspaceSlug: string
  workspaceName: string
  message: string
}

/**
 * Response from the public invite preview endpoint (unauthenticated).
 * Returns minimum information to display the invitation page.
 */
export interface InvitePreviewResponse {
  workspaceName: string
  inviterName: string
}

/**
 * Helper to get human-readable status label
 */
export function getInviteStatusLabel(status: InviteStatus): string {
  switch (status) {
    case InviteStatus.Pending:
      return 'Pending'
    case InviteStatus.Accepted:
      return 'Accepted'
    case InviteStatus.Expired:
      return 'Expired'
    case InviteStatus.Revoked:
      return 'Revoked'
    default:
      return 'Unknown'
  }
}

/**
 * Helper to get status badge color class
 */
export function getInviteStatusColor(status: InviteStatus): string {
  switch (status) {
    case InviteStatus.Pending:
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
    case InviteStatus.Accepted:
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
    case InviteStatus.Expired:
      return 'bg-muted text-muted-foreground'
    case InviteStatus.Revoked:
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
    default:
      return 'bg-muted text-muted-foreground'
  }
}

/**
 * Helper to check if invitation is still active
 */
export function isInviteActive(invite: UserInviteResponse): boolean {
  if (invite.status !== InviteStatus.Pending) {
    return false
  }

  const expiresAt = new Date(invite.expiresAt)
  const now = new Date()

  return expiresAt > now
}

/**
 * Helper to format expiration time remaining
 */
export function getInviteExpirationLabel(invite: UserInviteResponse): string {
  if (invite.status !== InviteStatus.Pending) {
    return ''
  }

  const expiresAt = new Date(invite.expiresAt)
  const now = new Date()
  const diffMs = expiresAt.getTime() - now.getTime()

  if (diffMs < 0) {
    return 'Expired'
  }

  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffHours / 24)

  if (diffDays > 1) {
    return `Expires in ${diffDays} days`
  } else if (diffHours > 1) {
    return `Expires in ${diffHours} hours`
  } else {
    return 'Expires soon'
  }
}
